import React from 'react';
import { Grid, Autocomplete, Typography, CircularProgress, TextField } from '@mui/material';
import { useWorkRulesQuery } from 'hooks/workRules/useWorkRulesQuery';
import { useTranslation } from 'react-i18next';
import { useWorkRulesForm } from 'hooks/workRules/useWorkRulesForm';
import ListTransfer from 'components/transfer/ListTransfer';

interface Props {
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
    form: ReturnType<typeof useWorkRulesForm>;
}

export const GeneralTab = ({ mode, form }: Props) => {
    const { t } = useTranslation();
    const { dataList, loading } = useWorkRulesQuery();

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <Autocomplete
                    id="rounding"
                    options={dataList.rounding_rules}
                    value={dataList.rounding_rules.find((r) => r.id === form.form.rounding_rule_id) ?? null}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    onChange={(_, newValue) => {
                        form.update('rounding_rule_id', newValue?.id ?? 0);
                    }}
                    loading={loading}
                    disabled={loading}
                    fullWidth
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('settings.work_rules.fields.tabs.tab1.rounding')}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <Autocomplete
                    id="exceptions"
                    options={dataList.exception_rules}
                    value={dataList.exception_rules.find((ex) => ex.id === form.form.excepcion_rule_id) ?? null}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    onChange={(_, newValue) => {
                        form.update('excepcion_rule_id', newValue?.id ?? 0);
                    }}
                    loading={loading}
                    disabled={loading}
                    fullWidth
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('settings.work_rules.fields.tabs.tab1.exception')}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="body2">
                    {t('settings.work_rules.fields.tabs.tab1.breaks')}
                    <br />
                    <br />
                </Typography>

                <ListTransfer
                    loading={loading}
                    allItems={dataList.break_rules}
                    right={form.form.break_rules}
                    setRight={(value: any) => form.update('break_rules', value)}
                    textLeft={t('settings.work_rules.fields.tabs.tab1.breaks_available')}
                    textRight={t('settings.work_rules.fields.tabs.tab1.selected_breaks')}
                    textSelected={t('settings.work_rules.fields.tabs.tab1.selected')}
                />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="body2">
                    {t('settings.work_rules.fields.tabs.tab1.bonuses_or_deductions')}
                    <br />
                    <br />
                </Typography>

                <ListTransfer
                    loading={loading}
                    allItems={dataList.bonus}
                    right={form.form.bonus}
                    setRight={(value: any) => form.update('bonus', value)}
                    textLeft={t('settings.work_rules.fields.tabs.tab1.bonuses_or_deductions_available')}
                    textRight={t('settings.work_rules.fields.tabs.tab1.selected_bonuses_or_deductions')}
                    textSelected={t('settings.work_rules.fields.tabs.tab1.selected')}
                />
            </Grid>
        </Grid>
    );
};
